import { CircularProgress, Stack, Box, Typography, Card, CardContent, Chip, Button, IconButton, Tooltip, useTheme, alpha } from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import SentinelLayout from './app/layout/SentinelLayout';
import AdminLayout from './app/admin/layout/AdminLayout';
import ProjectExplorer from './app/projects/page';
import ProjectDetail from './app/project/[appId]/page';
import ProjectWrapper from './app/project/ProjectWrapper';
import CustomPromptsPage from './app/admin/custom-prompts/page';
import TriggerExtractionPage from './app/admin/trigger-extraction/page';
import AdminProjectsPage from './app/admin/projects/page';
import AdminAiUsagePage from './app/admin/usage/page';
import { ROLE } from './lib/constants';
import ProjectSettingsLayout from './app/project-settings/layout/ProjectSettingsLayout';
import ProjectMetadataPage from './app/project-settings/pages/ProjectMetadataPage';
import DataSourcesPage from './app/project-settings/pages/DataSourcesPage';
import ConnectsPage from './app/project-settings/pages/ConnectsPage';
import TeamPage from './app/project-settings/pages/TeamPage';
import ApiSubscriptionPage from './app/project-settings/pages/ApiSubscriptionPage';
import ManagementAccessPage from './app/project-settings/pages/ManagementAccessPage';
import OnboardingPage from './app/project-settings/pages/OnboardingPage';
import UsagePage from './app/project-settings/pages/UsagePage';
import OnboardingCreatePage from './app/onboarding-secops/OnboardingCreatePage';
import OnboardingEditPage from './app/onboarding-secops/OnboardingEditPage';

import sentinelNetwork from './lib/api/sentinelNetwork';
import { DataProvider } from './lib/DataContext';
import { ProjectNameProvider } from './app/layout/ProjectNameContext';
import { ScanProvider } from './lib/ScanContext';
import ScanNotificationBar from './components/ScanNotificationBar';
import { ShieldOff, Copy, Check, ExternalLink, AlertTriangle, RefreshCw } from 'lucide-react';
import { useAuth } from './lib/auth/AuthProvider';

// ── No-Entitlement Guard Page ──────────────────────────────────────────────────
const ROLES_INFO = [
  {
    name: 'APP5784.SENTINEL_USERS',
    title: 'Sentinel Users',
    description: 'View and work on projects you\'ve been added to. Trigger scans, access project settings, and view security insights for your team\'s projects.',
  },
  {
    name: 'APP5784.SENTINEL_PROJECT_CREATION',
    title: 'Sentinel Project Creation',
    description: 'Everything in Sentinel Users, plus create and onboard new projects, add team members, and manage project configurations.',
  },
];

function CopyableRole({ name }: { name: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(name).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [name]);
  return (
    <Chip
      label={
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{name}</Typography>
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'} arrow>
            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
              {copied ? <Check size={13} /> : <Copy size={13} />}
            </IconButton>
          </Tooltip>
        </Stack>
      }
      variant="outlined"
      sx={{ height: 28, borderRadius: 1 }}
    />
  );
}

function SessionExpiredPage({ onRelogin }: { onRelogin: () => void }) {
  const theme = useTheme();
  return (
    <Stack width="100%" height="100vh" alignItems="center" justifyContent="center" px={3}>
      <Box sx={{
        width: 72, height: 72, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(theme.palette.warning.main, 0.10), mb: 3,
      }}>
        <AlertTriangle size={36} color={theme.palette.warning.main} />
      </Box>
      <Typography variant="h4" fontWeight={700}>Session Expired</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5, maxWidth: 480, textAlign: 'center' }}>
        Your session has expired. Please log in again to continue.
      </Typography>
      <Button
        variant="contained"
        size="large"
        sx={{ mt: 4, px: 4, textTransform: 'none' }}
        startIcon={<RefreshCw size={16} />}
        onClick={onRelogin}
      >
        Log in Again
      </Button>
    </Stack>
  );
}

function NoEntitlementPage({ isError }: { isError?: boolean }) {
  const theme = useTheme();

  if (isError) {
    return (
      <Stack width="100%" height="100vh" alignItems="center" justifyContent="center" px={3}>
        <Box sx={{
          width: 72, height: 72, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(theme.palette.warning.main, 0.10), mb: 3,
        }}>
          <AlertTriangle size={36} color={theme.palette.warning.main} />
        </Box>
        <Typography variant="h4" fontWeight={700}>Service Unavailable</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5, maxWidth: 520, textAlign: 'center' }}>
          Sentinel could not verify your entitlements right now. This is usually a temporary issue — please try refreshing the page.
        </Typography>
        <Button
          variant="contained"
          size="large"
          sx={{ mt: 4, px: 4, textTransform: 'none' }}
          startIcon={<RefreshCw size={16} />}
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </Button>
      </Stack>
    );
  }

  return (
    <Stack width="100%" height="100vh" alignItems="center" justifyContent="center" px={3}>
      <Box sx={{
        width: 72, height: 72, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(theme.palette.error.main, 0.08), mb: 3,
      }}>
        <ShieldOff size={36} color={theme.palette.error.main} />
      </Box>
      <Typography variant="h4" fontWeight={700}>Access Required</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5, maxWidth: 560, textAlign: 'center' }}>
        You need a Sentinel entitlement to use this application. Choose the role that fits your needs and request it via Alice.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 4, maxWidth: 960, width: '100%' }}>
        {ROLES_INFO.map((role) => (
          <Card
            key={role.name}
            variant="outlined"
            sx={{ flex: 1, borderRadius: 2, display: 'flex', flexDirection: 'column' }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{role.title}</Typography>
              <CopyableRole name={role.name} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {role.description}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Button
        variant="contained"
        size="large"
        sx={{ mt: 4, px: 4, textTransform: 'none' }}
        endIcon={<ExternalLink size={16} />}
        href="https://alice.mercedes-benz.com/access/access-request/myself"
        target="_blank"
        rel="noopener noreferrer"
      >
        Request Access on Alice
      </Button>
    </Stack>
  );
}

// How many consecutive automatic re-logins to attempt before giving up and
// showing the manual "Session Expired" page.
const REAUTH_ATTEMPTED = 'sentinel_reauth_count';
const MAX_REAUTH = 3;

function App() {
  const auth = useAuth();
  const [userData, setUserData] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    initializeAuth();
  }, []);

  /**
   * When the token is stale (empty roles or auth-denied response), redirect the
   * user to SSO with prompt=login. This forces an interactive login and ensures
   * the SSO issues a fresh token with all scopes (including entitlement_group).
   * Without prompt=login the SSO can silently reuse a session and return a token
   * that omits the entitlement_group scope, causing an infinite empty-roles loop.
   *
   * Up to MAX_REAUTH auto-redirects before showing the manual "Log in Again" page.
   */
  const handleStaleSession = (keepSpinnerRef: { value: boolean }) => {
    const count = parseInt(sessionStorage.getItem(REAUTH_ATTEMPTED) || '0', 10);
    if (count < MAX_REAUTH) {
      sessionStorage.setItem(REAUTH_ATTEMPTED, String(count + 1));
      keepSpinnerRef.value = true;
      // Wipe cached oidc state so automaticSilentRenew can't race us, then
      // redirect with prompt=login to force an interactive credential check.
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k?.startsWith('oidc.')) sessionStorage.removeItem(k!);
      }
      auth.signinRedirect({ prompt: 'login' });
    } else {
      sessionStorage.removeItem(REAUTH_ATTEMPTED);
      setSessionExpired(true);
    }
  };

  const initializeAuth = async () => {
    const spinnerRef = { value: false };
    try {
      const response = await sentinelNetwork.get('/entitlements');
      const data = response.data;

      // Only require data.roles to be present; user_email may be absent when
      // the silently-renewed access token drops the email claim.
      if (data?.roles !== undefined) {
        // Token is valid — load the app regardless of assigned roles.
        // Users with no Sentinel roles will see the project route with an empty
        // list and the add button (the earlier behaviour before the stale-session
        // guard was added).  The backend is the authoritative security boundary.
        sessionStorage.removeItem(REAUTH_ATTEMPTED);
        setUserData(data);
      }
    } catch (error: any) {
      console.error('Failed to fetch user entitlements:', error);
      const status = error?.response?.status;
      const responseType = error?.response?.data?.type;
      if (status && status >= 500) {
        // Explicit backend server error.
        setAuthError(true);
      } else if (status === 401 || (status === 403 && responseType === 'AUTH_ERROR') || !status) {
        // 401 = token missing/expired.
        // 403 with type=AUTH_ERROR = API Gateway authorizer Deny (token rejected by SSO).
        // No response (CORS error) = legacy Deny without CORS headers (should be rare now).
        // All three mean the token is stale — trigger a fresh login.
        handleStaleSession(spinnerRef);
      }
      // Other 4xx (RBAC denial with a different body) → fall through to "Access Required".
    } finally {
      if (!spinnerRef.value) setAuthLoading(false);
    }
  };

  const isAdmin = userData?.roles?.includes(ROLE.SENTINEL_POWER_USER);
  const hasEntitlement = [ROLE.SENTINEL_USERS, ROLE.SENTINEL_PROJECT_CREATION, ROLE.SENTINEL_POWER_USER].some(
    (r) => userData?.roles?.includes(r),
  );

  if (authLoading) {
    return (
      <Stack height="100vh" width="100vw" alignItems="center" justifyContent="center">
        <CircularProgress size={48} />
      </Stack>
    );
  }

  if (authError) {
    return <NoEntitlementPage isError />;
  }

  if (sessionExpired) {
    const handleRelogin = () => {
      sessionStorage.removeItem(REAUTH_ATTEMPTED);
      // Wipe oidc state and force an interactive SSO login with prompt=login.
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k?.startsWith('oidc.')) sessionStorage.removeItem(k!);
      }
      auth.signinRedirect({ prompt: 'login' });
    };
    return <SessionExpiredPage onRelogin={handleRelogin} />;
  }

  // Users with no Sentinel entitlement see the Access Required page.
  // Note: setUserData is still called for no-role users during initializeAuth
  // (to avoid the stale-session re-auth loop), but we gate the app UI here.
  if (!hasEntitlement) {
    return <NoEntitlementPage />;
  }

  return (
    <Stack height={'calc(100vh - 6px)'} width={'calc(100vw - 6px)'} p={'3px'}>
    <DataProvider
      initialData={userData || {}}
    >
      <ProjectNameProvider>
      <ScanProvider>
      <ScanNotificationBar />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/projects" replace />} />
          <Route path="/home" element={<Navigate to="/projects" replace />} />
          <Route path="/onboarding" element={<SentinelLayout />}>
            <Route path="search" element={<Navigate to="/onboarding/new" replace />} />
            <Route path="new" element={<OnboardingCreatePage />} />
            <Route path="edit/:appId" element={<OnboardingEditPage />} />
          </Route>
          <Route path="/projects" element={<SentinelLayout />}>
            <Route index element={<ProjectExplorer />} />
          </Route>
          <Route path="/projects/:appId" element={<ProjectWrapper />}>
            <Route element={<SentinelLayout />}>
              <Route index element={<ProjectDetail />} />
            </Route>
          </Route>
          <Route path="/dashboard" element={<Navigate to="/projects" replace />} />
          <Route path="/dashboard/:appId" element={<Navigate to="/projects" replace />} />
          {/* Deprecated: superseded by /projects and /dashboard, kept functional for settings + legacy links */}
          <Route path="/project/:appId" element={<ProjectWrapper />}>
            <Route element={<SentinelLayout />}>
              <Route index element={<ProjectDetail />} />
            </Route>
            <Route path="settings" element={<ProjectSettingsLayout />}>
              <Route index element={<ProjectMetadataPage />} />
              <Route path="metadata" element={<ProjectMetadataPage />} />
              <Route path="repositories" element={<DataSourcesPage />} />
              <Route path="connects" element={<ConnectsPage />} />
              <Route path="data-sources" element={<DataSourcesPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="management-access" element={<ManagementAccessPage />} />
              <Route path="api-subscription" element={<ApiSubscriptionPage />} />
              <Route path="usage" element={<UsagePage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
            </Route>
          </Route>
          {/* Detailed Scan — full sentinel metrics (SonarQube/BlackDuck/Security Score) for a project */}
          <Route path="/ai-details/:appId" element={<ProjectWrapper />}>
            <Route element={<SentinelLayout />}>
              <Route index element={<ProjectDetail />} />
            </Route>
          </Route>
          {isAdmin ? (
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<CustomPromptsPage />} />
              <Route path="prompts" element={<CustomPromptsPage />} />
              <Route path="extract" element={<TriggerExtractionPage />} />
              <Route path="projects" element={<AdminProjectsPage />} />
              <Route path="usage" element={<AdminAiUsagePage />} />
            </Route>
          ) : (
            <Route path="/admin/*" element={<Navigate to="/projects" replace />} />
          )}
        </Routes>
      </BrowserRouter>
      </ScanProvider>
      </ProjectNameProvider>
    </DataProvider>
    </Stack>
  );
}

export default App;
